import React, {useState} from "react"

import {H2, H5} from "@irati/ui/src/Typography/Heading"
import {FormattedMessage, injectIntl} from "@irati/utils/lang"
import {Body} from "@irati/ui/src/Typography"
import {Checkbox, ValidationButton} from "@irati/ui/src/FormItem"
import Button from "@irati/ui/src/Button"

import RecognitionService from "@irati/recognition/services/Recognition"
import tracking from "@irati-tracking/recognition/recognition"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Flex from "@irati/ui/src/Flex"
import Validation from "@irati/utils/validation"
import authentication from "@irati/auth"
import {
  BudgetCategoryRequest,
  FeedEntityType,
  GroupCelebration,
  GroupCelebrationStatus,
  PaymentCurrencyType,
  WalletEntityType
} from "@irati/types"
import {Col} from "@irati/ui/src/Layout"
import Form from "@irati/ui/src/Form"
import {useTheme} from "@irati/ui/src/global/useTheme"
import walletService from "@irati/wallet/graph/wallet"
import {useBudgetsList} from "@irati/wallet/hooks/budget"
import RecipientsList from "../../RecipientsList"
import PointsAssignmentWidget from "../RewardRecognition/PointsAssignmentWidget"
import {RewardInfo} from "../RewardRecognition/RewardRecognitionBanner"
import {StyledFlex, Separator} from "./styled"
import Details from "./Details"
import attachRewardToGroupCelebration from "@irati-web/recognition/src/utils/feed/attachReward"

interface Props extends InjectIntlProps {
  onClickButton: () => void
  onRewardCreated: () => void
  onErrorReward: () => void
  groupCelebration: GroupCelebration
  isMessageAdded?: boolean
}

const RewardGroupCelebration: React.FC<Props> = (props) => {
  const {formatMessage, onClickButton, groupCelebration, onRewardCreated, onErrorReward, isMessageAdded = false} = props
  const isUpdate = groupCelebration.status === GroupCelebrationStatus.Scheduled
  const theme = useTheme()
  const [submit, setSubmit] = useState<Promise<any>>()
  const [isChecked, setIsChecked] = useState(false)
  const [rewardInfo, setRewardInfo] = useState<RewardInfo>({
    pointsPerUser: isUpdate ? groupCelebration.rewardAmount : null,
    budgetId: isUpdate ? groupCelebration.budget?.categoryId : null,
    useUnassigned: isUpdate ? groupCelebration.budget?.useUnassigned : false
  })
  const {data, refetch: refetchBudgets} = useBudgetsList()
  const executeReward = async () => {
    if (!rewardInfo.pointsPerUser || !isChecked) {
      return
    }
    tracking.recognitionCtaClick({
      source: "scheduled_greeting_card",
      type: "add_reward"
    })
    const isUnassignedBudget = rewardInfo?.budgetId === data?.budgets?.unassigned.uuid
    const budgetDetails =
      isUnassignedBudget || !rewardInfo?.budgetId
        ? null
        : {
            categoryId: rewardInfo.budgetId,
            useUnassigned: rewardInfo.useUnassigned
          }
    const rewardPromise = attachRewardToGroupCelebration(
      [groupCelebration.receiver.uuid],
      rewardInfo.pointsPerUser,
      groupCelebration,
      FeedEntityType.GroupCelebration,
      budgetDetails
    )
      .then(() => {
        onRewardCreated()
        setTimeout(() => {
          refetchBudgets()
          // This call is gonna update the cached points for Reward Wallet. We put a timeout because sometimes the wallet is not coming back as updated.
          walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward, {fetchPolicy: "network-only"})
        }, 2000)
      })
      .catch(onErrorReward)
    setSubmit(rewardPromise)
  }
  const updateReward = () => {
    tracking.recognitionCtaClick({
      source: "scheduled_greeting_card",
      type: "update_reward"
    })
    const isUnassignedBudget = rewardInfo?.budgetId === data?.budgets?.unassigned.uuid
    let budgetDetails: BudgetCategoryRequest = {
      categoryId: "",
      useUnassigned: false
    }
    if (rewardInfo.pointsPerUser) {
      budgetDetails = {
        categoryId: isUnassignedBudget || !rewardInfo?.budgetId ? "" : rewardInfo.budgetId,
        useUnassigned: isUnassignedBudget || !rewardInfo?.budgetId ? true : rewardInfo.useUnassigned
      }
    }
    const rewardPromise = RecognitionService.updateGroupCelebration({
      groupCelebrationID: groupCelebration.uuid,
      rewardAmount: rewardInfo.pointsPerUser,
      budget: budgetDetails
    }).then(() => {
      onRewardCreated()
    })
    setSubmit(rewardPromise)
  }
  const onSkipButtonClick = () => {
    onClickButton()
    tracking.recognitionCtaClick({
      source: "confirmation_modal",
      type: "greeting_card_skip"
    })
  }
  if (!groupCelebration?.uuid) {
    return null
  }
  const mainActionLbl = isUpdate
    ? "recognition.popup.createRecognition.success.addRewardButton"
    : "recognition.popup.createRecognition.success.sendRewardButton"
  return (
    <StyledFlex>
      <Col xs={12} sm={4} padding="0">
        <Details groupCelebration={groupCelebration} isMessageAdded={isMessageAdded} />
      </Col>
      <Col xs={12} sm={1} padding="0">
        <Flex justifyContent="center" height="100%">
          <Separator />
        </Flex>
      </Col>
      <Col xs={12} sm={7} padding="0">
        <H2 textAlign="center" marginBottom={4}>
          <FormattedMessage id="recognition.popup.groupCelebration.addRewardHeader" />
        </H2>
        <Flex direction="column" gap={theme.spacing.three}>
          <Flex direction="column">
            <H5 marginBottom={2}>
              <FormattedMessage id="recognition.recipients.label" />
            </H5>
            <RecipientsList recipients={[groupCelebration.receiver]} />
          </Flex>
          <PointsAssignmentWidget rewardInfo={rewardInfo} onChange={setRewardInfo} totRecipients={1} />
          <Flex>
            <Form>
              <Checkbox
                name="rewardAccord"
                checked={isChecked}
                validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}
                onChange={() => setIsChecked(!isChecked)}>
                <Body small display="inline">
                  <FormattedMessage id="recognition.reward.admin.distribute.checkbox.title" />
                </Body>
              </Checkbox>
            </Form>
          </Flex>
        </Flex>
        <Flex gap={theme.spacing.two} direction="column">
          <ValidationButton primary small full onClick={isUpdate ? updateReward : executeReward} load={submit}>
            <FormattedMessage id={mainActionLbl} />
          </ValidationButton>
          <Button small full onClick={onSkipButtonClick}>
            <FormattedMessage id="platform.button.skip" />
          </Button>
        </Flex>
      </Col>
    </StyledFlex>
  )
}

export default injectIntl(RewardGroupCelebration)
